import type {
  LeagueInfo,
  LeagueProjectionAttributes,
  LeagueProjectionFilter,
  LeagueProjectionFilters,
  LeaguesResponse,
  ProjectionFilter,
  ProjectionFilterAttributeTypes
} from '@library/types/leagues';

import { uniq } from 'ramda';

export const rankThenNameSort = ({
  leagueProjectionAttributesList
}: {
  leagueProjectionAttributesList: Array<LeagueProjectionAttributes>;
}): Array<LeagueProjectionAttributes> => {
  const hasRankAttributes = leagueProjectionAttributesList.filter(
    (leagueProjectionAttributes) =>
      leagueProjectionAttributes.leagueProjectionFilterRank !== null
  );
  const notHasRankAttributes = leagueProjectionAttributesList.filter(
    (leagueProjectionAttributes) =>
      leagueProjectionAttributes.leagueProjectionFilterRank === null
  );
  return [
    ...hasRankAttributes.sort((a, b) =>
      a.leagueProjectionFilterRank !== b.leagueProjectionFilterRank
        ? (a.leagueProjectionFilterRank ?? 0) -
          (b.leagueProjectionFilterRank ?? 0)
        : a.leagueProjectionFilterName.localeCompare(
            b.leagueProjectionFilterName
          )
    ),
    ...notHasRankAttributes.sort((a, b) =>
      a.leagueProjectionFilterName.localeCompare(b.leagueProjectionFilterName)
    )
  ];
};

export const formatToLeagueProjectionAttributes = ({
  projectionFilter
}: {
  projectionFilter: ProjectionFilter;
}) => ({
  leagueProjectionFilterId: projectionFilter.id,
  leagueProjectionFilterName: projectionFilter.attributes.name,
  leagueProjectionFilterType: projectionFilter.attributes.type,
  leagueProjectionFilterRank: projectionFilter.attributes.rank
});

export const getLeagueProjectionFilters = ({
  leagueData,
  projectionFilters
}: {
  leagueData: LeaguesResponse;
  projectionFilters: LeagueProjectionFilters;
}): Array<LeagueProjectionAttributes> => {
  const allLeagueProjectionFilters = projectionFilters?.data?.map(
    (projectionFilter: LeagueProjectionFilter) => {
      const leagueFilterAttributes = leagueData.included.find(
        (leagueProjectionFilter) =>
          leagueProjectionFilter.id === projectionFilter.id
      );

      if (!leagueFilterAttributes) {
        return undefined;
      }

      const {
        id: leagueProjectionFilterId,
        attributes: {
          name: leagueProjectionFilterName,
          type: leagueProjectionFilterType,
          rank: leagueProjectionFilterRank,
          parent_id: leagueProjectionFilterParentId
        }
      } = leagueFilterAttributes;

      return {
        leagueProjectionFilterId,
        leagueProjectionFilterName,
        leagueProjectionFilterType,
        leagueProjectionFilterRank,
        leagueProjectionFilterParentId
      };
    }
  );

  /* This below two consts is to convince that the leagueFilters doesn't have any undefined 
    in the allLeagueProjectionFilters list  */
  const isLeagueProjectionFilter = (
    projectionFilter: LeagueProjectionAttributes | undefined
  ): projectionFilter is LeagueProjectionAttributes => {
    return (projectionFilter as LeagueProjectionAttributes) !== undefined;
  };

  return allLeagueProjectionFilters.filter(
    isLeagueProjectionFilter
  ) as LeagueProjectionAttributes[];
};

export const filterAttributeTypes = ({
  filterType,
  leagueProjectionFilters
}: {
  filterType: ProjectionFilterAttributeTypes;
  leagueProjectionFilters: Array<LeagueProjectionAttributes>;
}): Array<LeagueProjectionAttributes> => {
  const filterResults = leagueProjectionFilters.filter(
    (leagueProjectionFilter) =>
      leagueProjectionFilter.leagueProjectionFilterType === filterType
  );

  return filterResults.length ? uniq(filterResults) : [];
};

export const uniqueLeagueDurations = (
  leagueInfo: LeagueInfo | undefined
): Array<LeagueProjectionAttributes> => {
  return leagueInfo
    ? uniq(leagueInfo?.baseLeagueFilters.flatMap((league) => league.durations))
    : [];
};

export const isLeagueFullDuration = (durationName?: string): boolean => {
  return durationName === 'Full';
};
